import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GraduationCap, Lock, ArrowUpRight, Crown } from 'lucide-react';
import api from '../lib/api';
import { Spinner } from '../components/ui/Spinner';
import { UnlockInfoModal } from '../components/UnlockInfoModal';

export default function Mentorship() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    api
      .get('/mentorship/courses')
      .then(({ data }) => {
        setCourses(data.courses || []);
        setHasAccess(!!data.has_access);
      })
      .catch(() => setCourses([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="grid h-[60vh] place-items-center">
        <Spinner className="h-8 w-8" />
      </div>
    );

  return (
    <div className="space-y-10" data-testid="mentorship-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Crown size={22} className="text-emerald" />
            <h1 className="font-heading text-3xl font-bold tracking-tight text-white">Mentorship</h1>
          </div>
          <p className="mt-1 text-sm text-zinc-500">
            {hasAccess
              ? 'Your exclusive mentorship track — watch now.'
              : 'Premium 1-on-1 style mentorship courses. Unlock to gain access.'}
          </p>
        </div>
        {!hasAccess && (
          <button
            onClick={() => setShowInfo(true)}
            data-testid="mentorship-unlock-header"
            className="inline-flex items-center gap-2 rounded-full bg-emerald px-5 py-2.5 text-sm font-bold text-black transition-transform hover:scale-105"
          >
            Unlock Mentorship <ArrowUpRight size={16} />
          </button>
        )}
      </div>

      {courses.length > 0 ? (
        <section className="space-y-4">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {courses.map((c, i) => {
              const locked = !hasAccess;
              return (
                <motion.div
                  key={c.course_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="group relative overflow-hidden rounded-2xl border border-white/5 bg-ink-card text-left"
                >
                  <button
                    type="button"
                    disabled={locked}
                    onClick={() => !locked && navigate(`/app/mentorship/${c.course_id}`)}
                    data-testid={`mentorship-card-${c.course_id}`}
                    className={`w-full text-left transition-colors ${locked ? 'cursor-not-allowed' : 'hover:border-emerald/40'}`}
                  >
                    <div className="relative aspect-video overflow-hidden bg-ink-elevated">
                      {c.cover_url ? (
                        <img
                          src={c.cover_url}
                          alt={c.name}
                          className={`h-full w-full object-cover transition-transform duration-500 ${locked ? 'blur-[3px] brightness-50 scale-105' : 'group-hover:scale-105'}`}
                        />
                      ) : (
                        <div className={`grid h-full place-items-center text-zinc-600 ${locked ? 'blur-[2px]' : ''}`}>
                          <GraduationCap />
                        </div>
                      )}
                      {locked && (
                        <div className="absolute inset-0 grid place-items-center">
                          <div className="grid h-12 w-12 place-items-center rounded-full bg-black/70 text-emerald ring-1 ring-emerald/40">
                            <Lock size={22} />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading font-semibold text-white">{c.name}</h3>
                      <p className="mt-1 line-clamp-2 text-sm text-zinc-400">{c.description}</p>
                      <div className="mt-3 tnum text-xs text-zinc-500">
                        {c.lesson_count} lessons • {c.section_count} sections
                      </div>
                    </div>
                  </button>
                  {locked && (
                    <div className="border-t border-white/5 p-4">
                      <button
                        onClick={() => setShowInfo(true)}
                        data-testid={`mentorship-unlock-${c.course_id}`}
                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald/10 py-2.5 text-sm font-semibold text-emerald transition-colors hover:bg-emerald hover:text-black"
                      >
                        <Lock size={14} /> Unlock Mentorship
                      </button>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </section>
      ) : (
        <div className="grain relative rounded-3xl border border-white/5 bg-ink-card p-16 text-center">
          <p className="font-heading text-lg text-white">No mentorship courses yet</p>
          <p className="mt-2 text-sm text-zinc-500">Mentorship courses will appear here once published.</p>
        </div>
      )}

      <UnlockInfoModal open={showInfo} onClose={() => setShowInfo(false)} showDiscord={false} />
    </div>
  );
}
